import { transform, isEqual, isObject } from "lodash";

/*
 * This function takes two objects and returns an object with the keys that are different
 * between the two objects.
 *
 * This is useful when comparing a base object and an object updated from that base.
 *
 * For example, say we have an object from the server and we have a form that lets you
 * update these values:
 * program = {
 *  id: 1,
 *  name: 'Whatever',
 *  short_url: 'what_ever'
 * }
 * // user does some input and we have a form object like this:
 * form = {
 *  id: 1,
 *  name: 'Whatever',
 *  short_url: 'we'
 * }
 *
 * Now we want to only send the changes to the server.
 * changes = changeset(form, program)
 *
 * changes will look like this:
 *
 * {
 *  short_url: 'we'
 * }
 *
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const changeset = <T extends Record<string, any>>(object: T, base: T): Partial<T> => {
  const changes = (object: T | Partial<T>, base: T): Partial<T> => {
    return transform(
      object,
      (result: T | Partial<T>, value, key: keyof T | keyof Partial<T>) => {
        if (!isEqual(value, base[key])) {
          result[key] = isObject(value) && isObject(base[key]) ? changes(value, base[key]) : value;
        }
        return result;
      },
      {}
    );
  };

  return changes(object, base);
};
